import React, { useMemo } from 'react';
import { Box, NotificationCounterVariant, TabPanel, TabProps, TabsLayout, Text } from '@televet/kibble-ui';
import AppointmentTypeSettings from './components/ApppointmentTypeSettings/AppointmentTypeSettings';
import DirectBookingSettings from './components/DirectBookingSettings/DirectBookingSettings';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName } from 'shared/enums';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider/context/IntegrationsProvider';
import AiRecapSettings from './components/AiRecapSettings/AiRecapSettings';
import { useFailedWritebackAppointments } from 'shared/hooks/useFailedWritebackAppointments';

const AppointmentSettings = ({ hasPermission }: { hasPermission?: boolean }): JSX.Element => {
  const { isFeatureEnabled } = useFeatureFlag();
  const { isExportToPimsSupported } = useIntegrationsProvider();
  const { count, showAiRecapWritebackNotification } = useFailedWritebackAppointments();

  const tabList = useMemo(() => {
    const tabs: TabProps[] = [{ label: 'Appointment Types', color: 'text.default' }];
    if (isFeatureEnabled(FeatureFlagName.DirectBooking)) {
      tabs.push({ label: 'Direct Booking Availability' });
    }
    if (isExportToPimsSupported) {
      tabs.push({
        label: 'AI Recap',
        notificationCount: count,
        notificationCounterVariant: NotificationCounterVariant.Red,
        showNotificationCounter: showAiRecapWritebackNotification,
      });
    }
    return tabs;
  }, [count, isExportToPimsSupported, isFeatureEnabled, showAiRecapWritebackNotification]);

  const tabPanels = useMemo(() => {
    const tabPanels: JSX.Element[] = [
      <TabPanel key="appointment-type-settings-tab">
        <AppointmentTypeSettings hasPermission={hasPermission} />
      </TabPanel>,
    ];
    if (isFeatureEnabled(FeatureFlagName.DirectBooking)) {
      tabPanels.push(
        <TabPanel key="direct-booking-settings-tab">
          <DirectBookingSettings />
        </TabPanel>,
      );
    }
    if (isExportToPimsSupported) {
      tabPanels.push(
        <TabPanel key="ai-recap-settings-tab">
          <AiRecapSettings />
        </TabPanel>,
      );
    }
    return tabPanels;
  }, [hasPermission, isExportToPimsSupported, isFeatureEnabled]);

  return (
    <Box pb={10}>
      <Box my={5}>
        <Text size="sm" variant="subtle">
          Configure appointment type {isFeatureEnabled(FeatureFlagName.DirectBooking) ? 'and direct booking ' : ''}
          settings.
        </Text>
      </Box>
      <TabsLayout tabList={tabList} tabPanels={tabPanels} />
    </Box>
  );
};

export default AppointmentSettings;
